import { useMemo, useState } from 'react';
import { feedItems, feedCats, type FeedCategory } from '@/mocks/feed';

const catStyle: Record<string, { chip: string; dot: string }> = {
  娱乐: { chip: 'bg-accent-50 text-accent-700 ring-accent-200', dot: 'bg-accent-500' },
  影视: { chip: 'bg-primary-50 text-primary-700 ring-primary-200', dot: 'bg-primary-500' },
  综艺: { chip: 'bg-secondary-50 text-secondary-800 ring-secondary-200', dot: 'bg-secondary-500' },
  社会: { chip: 'bg-accent-50 text-accent-700 ring-accent-200', dot: 'bg-accent-600' },
  资讯: { chip: 'bg-primary-50 text-primary-700 ring-primary-200', dot: 'bg-primary-600' },
  热梗: { chip: 'bg-secondary-50 text-secondary-800 ring-secondary-200', dot: 'bg-secondary-600' },
};

export default function FeedSection() {
  const [cat, setCat] = useState<FeedCategory>('全部');
  const [kw, setKw] = useState('');

  const list = useMemo(() => {
    const trimmed = kw.trim().toLowerCase();
    return feedItems.filter((it) => {
      const matchCat = cat === '全部' || it.cat === cat;
      const matchKw = !trimmed || it.title.toLowerCase().includes(trimmed) || it.summary.toLowerCase().includes(trimmed);
      return matchCat && matchKw;
    });
  }, [cat, kw]);

  const count = (c: FeedCategory) => (c === '全部' ? feedItems.length : feedItems.filter((i) => i.cat === c).length);

  return (
    <section id="feed" className="scroll-mt-20 bg-background-100 py-12 md:py-16">
      <div className="mx-auto max-w-[1200px] px-4 md:px-6">
        <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full bg-accent-50 px-3 py-1 text-xs font-bold text-accent-700 ring-1 ring-accent-200">
              <i className="ri-flashlight-line" />
              实时速报
            </span>
            <h2 className="mt-3 font-heading text-3xl text-foreground-950 md:text-4xl">
              滚动更新，最新鲜的料都在这里
            </h2>
            <p className="mt-2 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
              突发消息与持续发酵的热点会随事件进展持续补充，按频道筛选或输入关键词，快速锁定你真正关心的动态。
            </p>
          </div>

          <label className="group flex items-center gap-2 rounded-full border border-background-200 bg-background-50 px-4 py-2.5 transition focus-within:border-primary-300 focus-within:ring-2 focus-within:ring-primary-200/60 md:w-72">
            <i className="ri-search-line text-foreground-400" style={{ fontSize: 16 }} />
            <input
              type="text"
              value={kw}
              onChange={(e) => setKw(e.target.value)}
              placeholder="搜索速报关键词"
              className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
            />
            {kw && (
              <button
                type="button"
                onClick={() => setKw('')}
                className="text-foreground-400 hover:text-foreground-700"
                aria-label="清空搜索"
              >
                <i className="ri-close-circle-fill" />
              </button>
            )}
          </label>
        </div>

        <div className="mt-6 flex flex-wrap items-center gap-2">
          {feedCats.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setCat(c)}
              className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-1.5 py-1.5 transition ${
                cat === c
                  ? 'bg-primary-500 text-background-50'
                  : 'bg-background-50 text-foreground-700 ring-1 ring-background-200 hover:bg-background-100'
              }`}
            >
              <span className="px-2.5 text-sm font-medium">{c}</span>
              <span
                className={`rounded-full px-2 py-0.5 text-[11px] leading-none ${
                  cat === c ? 'bg-background-50/20 text-background-50' : 'bg-background-100 text-foreground-500'
                }`}
              >
                {count(c)}
              </span>
            </button>
          ))}
        </div>

        <div className="mt-7">
          {list.length === 0 ? (
            <div className="grid place-items-center rounded-3xl border border-dashed border-background-200 bg-background-50 px-6 py-16 text-center">
              <i className="ri-inbox-2-line text-4xl text-background-300" />
              <p className="mt-3 text-sm font-medium text-foreground-600">暂时没有匹配的速报，换个关键词或频道试试</p>
              <button
                type="button"
                onClick={() => {
                  setKw('');
                  setCat('全部');
                }}
                className="mt-4 whitespace-nowrap rounded-full bg-primary-500 px-5 py-2 text-sm font-semibold text-background-50 hover:bg-primary-600"
              >
                查看全部速报
              </button>
            </div>
          ) : (
            <ul className="grid gap-3 md:grid-cols-2">
              {list.map((it) => {
                const s = catStyle[it.cat] || catStyle['娱乐'];
                return (
                  <li key={it.id}>
                    <article className="group relative h-full overflow-hidden rounded-2xl border border-background-200/80 bg-background-50 p-5 transition hover:-translate-y-1 hover:border-primary-200 hover:shadow-none">
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2">
                          <span className={`h-2 w-2 rounded-full ${s.dot}`} />
                          <span className="text-xs font-bold text-foreground-800">{it.cat}</span>
                          <span className="rounded-md bg-background-100 px-2 py-0.5 text-[11px] font-medium text-foreground-500">
                            {it.tag}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          {it.badge && (
                            <span
                              className={`rounded-md px-1.5 py-0.5 text-[11px] font-bold leading-none ${
                                it.badge === '爆'
                                  ? 'bg-accent-500 text-background-50'
                                  : it.badge === '热'
                                  ? 'bg-secondary-500 text-background-50'
                                  : 'bg-primary-500 text-background-50'
                              }`}
                            >
                              {it.badge}
                            </span>
                          )}
                          <span className="inline-flex items-center gap-1 text-[11px] text-foreground-400">
                            <i className="ri-time-line" />
                            {it.time}
                          </span>
                        </div>
                      </div>

                      <h3 className="mt-3 text-base font-bold leading-snug text-foreground-950 group-hover:text-primary-700 md:text-lg">
                        {it.title}
                      </h3>
                      <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-600">{it.summary}</p>

                      <div className="mt-4 flex items-center justify-between border-t border-background-100 pt-3">
                        <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-accent-600">
                          <i className="ri-fire-fill" />
                          {it.heat}
                        </span>
                        <span className="inline-flex items-center gap-1 text-[11px] font-medium text-primary-600 opacity-0 transition group-hover:opacity-100">
                          查看详情
                          <i className="ri-arrow-right-s-line" />
                        </span>
                      </div>
                    </article>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </section>
  );
}